import React, { useState } from "react";

import { useRouter } from "next/router";

import { t, Trans } from "@lingui/macro";
import { Button, Col, Form, message, Row } from "antd";
import dayjs from "dayjs";
import styled from "styled-components";
import useSWR from "swr";

import AppLayout from "layout/app";

import FuelCardsForms from "components/fuel-cards-forms";
import MoreButton from "components/more-button";
import { ActionMenu } from "components/navigations/header";

import { useApplicationContext } from "contexts/app";

import { isFormValueChanged } from "utils/form-utils";
import { isUUID } from "utils/uuid-utils";

const permissionKey = "can_manage_fuel_cards";

const Container = styled(Row)`
  height: fit-content;
  padding: ${(props) => {
    return props.theme.sizeMS;
  }}px;
  background: ${(props) => {
    return props.theme.colorBgContainer;
  }};
`;

const FuelCard = () => {
  const router = useRouter();
  const [form] = Form.useForm();
  const { httpClient, configuration } = useApplicationContext();
  const [changed, setChanged] = useState(false);

  const { id } = router.query;
  const fieldCardId = id?.toString();

  const isUpdate = isUUID(id?.toString());

  const { data, error } = useSWR(isUpdate && `fuel_cards/${fieldCardId}/`);
  const dataLoading = isUpdate && data === undefined && error === undefined;

  const onRemoveFuelCard = async (id?: string) => {
    if (id) {
      await httpClient.remove(`fuel_cards/${id}/`, {
        successHandler: () => {
          message.success(t`Fuel card has been deleted`);
          router.push(`/settings/fuel-cards/`);
        },
      });
    }
  };

  const hasPermission = configuration?.account_permissions?.includes(permissionKey);

  return (
    <AppLayout
      pageTitle={t`Fuel cards`}
      loading={dataLoading}
      label={data?.number}
      autoScroll={true}
      fullscreen={false}
    >
      <ActionMenu
        menus={[
          changed && (
            <Button
              type="default"
              onClick={() => {
                form.setFieldsValue({
                  ...data,
                  expiration_date: data.expiration_date ? dayjs(data.expiration_date) : data.expiration_date,
                });
                setChanged(false);
              }}
            >
              <Trans>Discard</Trans>
            </Button>
          ),
          <Button
            type="primary"
            onClick={() => {
              form.submit();
            }}
          >
            <Trans>Save</Trans>
          </Button>,
          <MoreButton
            items={[
              {
                key: 0,
                label: t`Delete fuel card`,
                onClick: async () => {
                  await onRemoveFuelCard(id?.toString());
                },
                danger: true,
              },
            ]}
          />,
        ]}
      />
      <Container>
        <Col xxl={12} xl={12} md={24}>
          <FuelCardsForms
            onFinish={() => {
              router.push("/settings/fuel-cards/");
            }}
            fuelCardId={fieldCardId}
            form={form}
            hasPermission={hasPermission}
            data={data}
            onFieldChanged={(v) => {
              setChanged(isFormValueChanged(v)(null, data || {}));
            }}
          />
        </Col>
      </Container>
    </AppLayout>
  );
};

export default FuelCard;
